import React from 'react';
import { useState } from 'react';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { SettingsCoordinator } from '~settings-coordinator/SettingsCoordinator';
import { RemoteRefreshAgents } from '~src/extractRefresh/resources/RemoteRefreshAgents';
import { ErrorReason } from '~src/sessionManagement/SessionManagementService';
import {
  DataConnectSiteSettings,
  DataConnectSiteSettingsProps,
  SiteDataConnectSettingsForSiteAdminProps
} from '~src/settings/dataconnect-settings/DataConnectSiteSettings';
import { IGetBaasEdgePoolsResult } from '~src/settings/dataconnect-settings/serverApi/requests/GetBaasEdgePoolsRequest';
import { IRemoteAgentConnectionStatus } from '~src/settings/dataconnect-settings/serverApi/requests/GetSiteRemoteAgentsConnectionStatusRequest';
import { BaasEdgePool } from '~src/settings/dataconnect-settings/serverApi/resources/BaasEdgePool';

import { BaasEdgePoolsPayload } from './sections/types/BaasEdgePoolsPayload';
import { Cluster } from './sections/types/Cluster';
import { ClusterStatusPollService } from './sections/utils/ClusterStatusPollService';
import { getGeneralErrorMessage } from './sections/utils/EdgePoolErrorUtilities';
import { IGetBaasClusterDetailsResult } from './serverApi/requests/GetBaasClusterDetailsRequest';

const initialRemoteAgentConnectionStatuses: IRemoteAgentConnectionStatus[] = [];
const initialEdgePools: BaasEdgePoolsPayload = {
  userDefinedPools: {},
  blockedDomains: {},
  siteId: ''
};

const clusterMap: Record<string, Cluster> = {};

/**
 * Creates a settings coordinator with the site DataConnect settings
 */
export function DataConnectSiteSettingsCoordinator(props: SiteDataConnectSettingsForSiteAdminProps) {
  const [remoteAgentConnectionStatuses, setRemoteAgentConnectionStatuses] = useState(initialRemoteAgentConnectionStatuses);
  const [baasEdgePools, setBaasEdgePools] = useState(initialEdgePools);
  const [hasLoadedBaasEdgePools, setHasLoadedBaasEdgePools] = useState(false);
  const [clusters, setClusters] = useState(clusterMap);

  const refreshBaasEdgePools = React.useCallback(() => {
    FeatureFlags.edgeMultiPoolEnabled && BaasEdgePool.getBaasPools({ siteId: props.settings.siteId })
      .then((result: IGetBaasEdgePoolsResult) => {
        if (result.success) {
          setBaasEdgePools(result.success);
          setClusters(wrapperClusters(result.success));
        }
        if (result.errors) {
          ClusterStatusPollService.cancelClusterDetailsStatsPolling();
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message)
          });
        }
      });
  }, [props.settings.siteId]);


  React.useEffect(() => {
    RemoteRefreshAgents.getSiteRemoteAgentsConnectionStatus()
      .then((result: IRemoteAgentConnectionStatus[]) => {
        setRemoteAgentConnectionStatuses(result);
      });
  }, []);

  React.useEffect(() => {
    FeatureFlags.edgeMultiPoolEnabled && BaasEdgePool.getBaasPools({ siteId: props.settings.siteId })
      .then((result: IGetBaasEdgePoolsResult) => {
        if (result.success) {
          setBaasEdgePools(result.success);
          setHasLoadedBaasEdgePools(true);
          setClusters(wrapperClusters(result.success));
        }
        if (result.errors) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message)
          });
        }
      });
  }, [props.settings.siteId]);

  React.useEffect(
    () => {
      if (hasLoadedBaasEdgePools && Object.keys(clusters).length>0) {
        let shouldSetState = true;
        const callbackWithResult = (result: IGetBaasClusterDetailsResult) => {
          if (result?.errors) {
            // any error happens, stop the poll service
            ClusterStatusPollService.cancelClusterDetailsStatsPolling();
          } else {

            Object.keys(clusters).forEach(clusterId => {
              if (!_.isEqual(clusters[clusterId],result.success[clusterId])) {
                //trigger the reload
                refreshBaasEdgePools();
              }
          });
          }
        };
        const callbackWithError = (reason: ErrorReason) => {
          // if the request was canncelled, don't stop poller
          if (ErrorReason.Canceled !== reason) {
            ClusterStatusPollService.cancelClusterDetailsStatsPolling();
          }
        };

        ClusterStatusPollService.pollClusterDetailsStats(clusters, props.settings.siteId, callbackWithResult).then(
          callbackWithResult, // success
          callbackWithError, // error
        );

        return () => {
          ClusterStatusPollService.cancelClusterDetailsStatsPolling();
          shouldSetState = false;
        };
      }
    },
    [hasLoadedBaasEdgePools, clusters, props.settings.siteId, refreshBaasEdgePools]
  );

  const newProps: DataConnectSiteSettingsProps = {
    settings: props.settings,
    connectionStatuses: remoteAgentConnectionStatuses,
    baasEdgePools: baasEdgePools,
    refreshBaasEdgePools,
    hasLoadedBaasEdgePools
  };

  return (
    <SettingsCoordinator>
      <DataConnectSiteSettings {...newProps} />
    </SettingsCoordinator>
  );
}

export function wrapperClusters(edgePools: BaasEdgePoolsPayload): Record<string, Cluster> {
  const pools = Object.values(edgePools.userDefinedPools);
  const result: Record<string, Cluster> = {};
  pools.forEach(pool => {
    result[pool.clusterId] = pool.cluster;
  });
  return result;
}